import type { ReactNode } from 'react';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { createPromiseWithResolvers } from '@/utils/promise';

interface ResolutionButton {
  label: string;
  resolution: string;
  hidden?: boolean;
  type?: 'permissive' | 'destructive' | 'informative';
}

export interface IncomingPrompt {
  key: string;
  title: ReactNode;
  content?: ReactNode;
  resolveButtonLabel?: string;
  rejectButtonLabel?: string;
  resolveButtonType?: 'permissive' | 'destructive' | 'informative';
  resolutionButtons?: Array<ResolutionButton>;
  promise?: () => Promise<unknown> | unknown;
}

export interface Prompt extends IncomingPrompt {
  isLoading: boolean;
  isPending: boolean;
  resolvers: {
    resolve: (value: boolean | string | PromiseLike<boolean | string>) => void;
    reject: () => void;
  };
}

interface PromptState {
  prompts: Record<string, Prompt>;
}

interface PromptActions {
  addPrompt: (prompt: IncomingPrompt) => Promise<boolean | string>;
  removePrompt: (key: string, shouldReject?: boolean) => void;
  clearPrompts: (shouldReject?: boolean) => void;
}

type PromptStore = PromptState & PromptActions;

export const usePrompterStore = create<PromptStore>()(
  immer((set, get) => ({
    prompts: {},

    addPrompt: async (prompt) => {
      const { promise, resolve, reject } = createPromiseWithResolvers<
        boolean | string | PromiseLike<boolean | string>
      >();

      set((state) => {
        state.prompts[prompt.key] = {
          ...prompt,
          isLoading: false,
          isPending: true,
          resolvers: { resolve, reject },
        };
      });

      return promise.then(async (value) => {
        if (prompt.promise && Boolean(value)) {
          set((state) => {
            const instance = state.prompts[prompt.key];
            if (instance) {
              instance.isPending = false;
              instance.isLoading = true;
            }
          });
          try {
            await prompt.promise();
          } catch (e) {
            console.error(e);
            reject();
          }
        }

        get().removePrompt(prompt.key, false);
        return value;
      });
    },

    removePrompt: (key, shouldReject = true) => {
      set((state) => {
        if (state.prompts[key]) {
          if (shouldReject) state.prompts[key].resolvers.reject();
          delete state.prompts[key];
        }
      });
    },

    clearPrompts: (shouldReject = true) => {
      set((state) => {
        for (const key in state.prompts) {
          if (state.prompts[key]) {
            if (shouldReject) state.prompts[key].resolvers.reject();
            delete state.prompts[key];
          }
        }
      });
    },
  })),
);

export const makePrompt = usePrompterStore.getState().addPrompt;
